import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";
import {
  IconButton,
  Menu,
  MenuItem,
  Typography,
  ListItemText,
  Box,
  Tooltip,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Divider,
  Chip,
  Stack,
  Collapse,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import Check from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useAppSelector, useAppDispatch } from "@store/hooks";
import { setWorkspaceData } from "@store/slices/workspaceSlice";
import {
  DEFAULT_CATEGORIES,
  MAX_CATEGORIES,
  getCategoryTextColor,
} from "@shared/utils/defaultCategories";
import {
  openResourceManagementModal,
  closeResourceManagementModal,
  selectResourceManagementModalOpen,
  selectResourceManagementModalTab,
  selectSelectedOrgId,
  setSelectedOrgId,
  selectBaseView,
} from "@store/slices/viewSlice";
import { selectCurrentUserOrgs } from "@store/slices/orgsSlice";
import { getWorkspaces, createWorkspace } from "@services/api";
import { Category, Workspace } from "@/types/models";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import { saveAllDirtyResources } from "@store/thunks/workspaceThunks";
import { ResourceManagementModal } from "../../resources/components/ResourceManagementModal";
import { ManageMembersModal } from "./ManageMembersModal";
import PeopleIcon from "@mui/icons-material/People";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

const WorkspaceDropdown = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [showAllWorkspaces, setShowAllWorkspaces] = useState(false);
  const [draftCategories, setDraftCategories] = useImmer<Category[]>([
    ...DEFAULT_CATEGORIES,
  ]);
  const [newCategoryLabel, setNewCategoryLabel] = useState("");
  const open = Boolean(anchorEl);

  const manageResourcesOpen = useAppSelector(selectResourceManagementModalOpen);
  const manageResourcesTab = useAppSelector(selectResourceManagementModalTab);
  const selectedOrgId = useAppSelector(selectSelectedOrgId);
  const userOrgs = useAppSelector(selectCurrentUserOrgs);
  const baseView = useAppSelector(selectBaseView);

  const { userId } = useAppSelector((state) => state.user);
  const { workspace_id: currentWorkspaceId } = useAppSelector(
    (state) => state.workspace
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Filter the workspace list to the active context. Personal context
  // (null) shows workspaces with org_id === null; org context shows
  // workspaces with org_id === selectedOrgId.
  const contextWorkspaces = workspaces.filter((w) =>
    selectedOrgId === null ? !w.org_id : w.org_id === selectedOrgId
  );
  const activeOrg = userOrgs.find((o) => o.org_id === selectedOrgId);
  const contextLabel = activeOrg ? activeOrg.name : "Personal";

  // Cap the visible list at a small preview by default — for users with
  // many workspaces, scrolling past dozens of entries to reach the admin
  // actions is bad UX. The currently-selected workspace always appears
  // in the preview so the user can see what they're on.
  const WORKSPACE_PREVIEW_COUNT = 5;
  const hasMoreWorkspaces =
    contextWorkspaces.length > WORKSPACE_PREVIEW_COUNT;
  const visibleWorkspaces =
    showAllWorkspaces || !hasMoreWorkspaces
      ? contextWorkspaces
      : (() => {
          const preview = contextWorkspaces.slice(0, WORKSPACE_PREVIEW_COUNT);
          if (
            preview.some((w) => w.workspace_id === currentWorkspaceId) ||
            !currentWorkspaceId
          ) {
            return preview;
          }
          const current = contextWorkspaces.find(
            (w) => w.workspace_id === currentWorkspaceId
          );
          return current
            ? [current, ...preview.slice(0, WORKSPACE_PREVIEW_COUNT - 1)]
            : preview;
        })();

  const fetchWorkspaces = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    try {
      console.log("Fetching workspaces for user:", userId);
      const result = await getWorkspaces();
      if (!result.success) {
        console.error("Error fetching workspaces:", result.error.message);
      } else {
        setWorkspaces(Array.isArray(result.data) ? result.data : []);
      }
    } catch (error) {
      console.error("Error fetching workspaces:", error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId && open) {
      fetchWorkspaces();
    }
  }, [userId, open, fetchWorkspaces]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleWorkspaceSelect = async (workspace: Workspace) => {
    // Save all dirty resources BEFORE switching workspaces
    // This prevents data loss if user has unsaved changes
    try {
      await dispatch(saveAllDirtyResources()).unwrap();
    } catch (error) {
      console.error('Error saving before workspace switch:', error);
      // Continue with switch even if save fails (user might want to discard changes)
    }

    // Switch to new workspace. Push the full payload so shared_data and
    // user_data also update; otherwise switching keeps the prior workspace's
    // categories in Redux.
    dispatch(
      setWorkspaceData({
        workspace_id: workspace.workspace_id,
        workspace_name: workspace.workspace_name,
        shared_data: workspace.shared_data,
        user_data: workspace.user_data,
        workspace_permissions: workspace.workspace_permissions,
      })
    );
    handleClose();
  };

  const handleManageResourcesClick = () => {
    dispatch(
      openResourceManagementModal(baseView === "canvas" ? "canvases" : "readings")
    );
    handleClose(); // Close the dropdown menu
  };

  const [manageMembersOpen, setManageMembersOpen] = useState(false);
  const handleManageMembersClick = () => {
    setManageMembersOpen(true);
    handleClose();
  };

  const handleCreateWorkspaceClick = () => {
    setCreateDialogOpen(true);
    handleClose(); // Close the dropdown menu
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    setNewWorkspaceName("");
    setCreateError("");
    setCustomizeOpen(false);
    setDraftCategories([...DEFAULT_CATEGORIES]);
    setNewCategoryLabel("");
  };

  const handleAddCategory = () => {
    if (draftCategories.length >= MAX_CATEGORIES || newCategoryLabel === "") {
      return;
    }
    setDraftCategories((draft) => {
      draft.push({
        label: newCategoryLabel,
        background: `#${Math.floor(Math.random() * 16777215)
          .toString(16)
          .padStart(6, "0")}`,
      });
    });
    setNewCategoryLabel("");
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) {
      setCreateError("Workspace name is required");
      return;
    }

    setCreating(true);
    setCreateError("");

    try {
      const result = await createWorkspace(
        newWorkspaceName.trim(),
        draftCategories,
        selectedOrgId
      );

      if (!result.success) {
        setCreateError(result.error.message);
        setCreating(false);
        return;
      }

      // Save all dirty resources before switching
      try {
        await dispatch(saveAllDirtyResources()).unwrap();
      } catch (error) {
        console.error('Error saving before workspace switch:', error);
      }

      // Switch to the newly created workspace. The backend applies default
      // categories on the server side, so use the full response (not just the
      // id) to seed Redux with shared_data and user_data.
      const newWorkspace = result.data;
      dispatch(
        setWorkspaceData({
          workspace_id: newWorkspace.workspace_id,
          workspace_name: newWorkspace.workspace_name,
          shared_data: newWorkspace.shared_data,
          user_data: newWorkspace.user_data,
          workspace_permissions: newWorkspace.workspace_permissions,
        })
      );

      // Close dialog and reset state
      handleCreateDialogClose();

      // Refresh the workspaces list
      await fetchWorkspaces();
    } catch (error: any) {
      setCreateError(error.message || "Failed to create workspace");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box>
      <Tooltip title="Switch workspace">
        <IconButton
          onClick={handleClick}
          size="medium"
          sx={{
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
          aria-controls={open ? "workspace-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <FolderIcon />
        </IconButton>
      </Tooltip>

      <Menu
        id="workspace-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "workspace-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            style: {
              maxHeight: 400,
              minWidth: 250,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Context
          </Typography>
        </Box>
        <MenuItem
          selected={selectedOrgId === null}
          onClick={() => dispatch(setSelectedOrgId(null))}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Personal</Typography>
          </ListItemText>
          {selectedOrgId === null && <Check fontSize="small" />}
        </MenuItem>
        {userOrgs.map((org) => (
          <MenuItem
            key={org.org_id}
            selected={selectedOrgId === org.org_id}
            onClick={() => dispatch(setSelectedOrgId(org.org_id))}
          >
            <ListItemIcon>
              <BusinessIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">{org.name}</Typography>
            </ListItemText>
            {selectedOrgId === org.org_id && <Check fontSize="small" />}
          </MenuItem>
        ))}
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {contextLabel} workspaces
          </Typography>
        </Box>
        {loading ? (
          <MenuItem disabled>
            <ListItemText primary="Loading workspaces..." />
          </MenuItem>
        ) : visibleWorkspaces.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary={`No workspaces in ${contextLabel}`} />
          </MenuItem>
        ) : (
          visibleWorkspaces.map((workspace) => (
            <MenuItem
              key={workspace.workspace_id}
              onClick={() => handleWorkspaceSelect(workspace)}
              disabled={workspace.workspace_id === currentWorkspaceId}
            >
              {workspace.workspace_id === currentWorkspaceId && (
                <ListItemIcon>
                  <Check />
                </ListItemIcon>
              )}
              <ListItemText>
                <Typography variant="body2">
                  {workspace.workspace_name}
                </Typography>
              </ListItemText>
            </MenuItem>
          ))
        )}
        {hasMoreWorkspaces && (
          <MenuItem onClick={() => setShowAllWorkspaces((v) => !v)}>
            <ListItemIcon>
              {showAllWorkspaces ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2" color="primary">
                {showAllWorkspaces
                  ? `Show fewer`
                  : `Show all (${contextWorkspaces.length})`}
              </Typography>
            </ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleManageResourcesClick}>
          <ListItemIcon>
            <FolderOpenIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Manage Resources</Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleManageMembersClick}
          disabled={!currentWorkspaceId}
        >
          <ListItemIcon>
            <PeopleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Manage Members</Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCreateWorkspaceClick}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Create Workspace</Typography>
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            navigate("/workspaces");
            handleClose();
          }}
          disabled={!currentWorkspaceId}
        >
          <ListItemIcon>
            <SettingsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Open Workspace Admin →</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Workspace</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A workspace contains all your research materials - PDFs, argument diagrams, and papers.
          </Typography>
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Create in:
            </Typography>
            <Chip
              size="small"
              icon={activeOrg ? <BusinessIcon /> : <PersonIcon />}
              label={contextLabel}
            />
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="Workspace Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newWorkspaceName}
            onChange={(e) => setNewWorkspaceName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !creating) {
                handleCreateWorkspace();
              }
            }}
            error={!!createError}
            helperText={createError || "Give your workspace a descriptive name"}
            disabled={creating}
            placeholder="e.g., PhD Thesis Research, Spring 2024 Course"
          />
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Default annotation categories
              </Typography>
              <Button
                size="small"
                onClick={() => setCustomizeOpen((v) => !v)}
                endIcon={customizeOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                disabled={creating}
              >
                Customize
              </Button>
            </Box>

            <Collapse in={!customizeOpen} unmountOnExit>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1 }}
              >
                {draftCategories.map((category) => (
                  <Chip
                    key={category.label}
                    label={category.label}
                    size="small"
                    sx={{
                      backgroundColor: category.background,
                      color: getCategoryTextColor(category.background),
                    }}
                  />
                ))}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                You can customize these categories later in workspace settings.
              </Typography>
            </Collapse>

            <Collapse in={customizeOpen} unmountOnExit>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {draftCategories.map((category, idx) => (
                  <Stack
                    key={idx}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <input
                      type="color"
                      value={category.background}
                      onChange={(e) =>
                        setDraftCategories((draft) => {
                          const c = draft[idx];
                          if (c) c.background = e.target.value;
                        })
                      }
                      aria-label={`Color for ${category.label}`}
                      disabled={creating}
                    />
                    <TextField
                      size="small"
                      value={category.label}
                      onChange={(e) =>
                        setDraftCategories((draft) => {
                          const c = draft[idx];
                          if (c) c.label = e.target.value;
                        })
                      }
                      sx={{ flexGrow: 1 }}
                      disabled={creating}
                    />
                    <IconButton
                      size="small"
                      onClick={() =>
                        setDraftCategories((draft) => {
                          draft.splice(idx, 1);
                        })
                      }
                      aria-label={`Remove ${category.label}`}
                      disabled={creating}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Add category"
                    value={newCategoryLabel}
                    onChange={(e) => setNewCategoryLabel(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCategory();
                      }
                    }}
                    sx={{ flexGrow: 1 }}
                    disabled={
                      creating || draftCategories.length >= MAX_CATEGORIES
                    }
                  />
                  <Button
                    size="small"
                    onClick={handleAddCategory}
                    disabled={
                      creating ||
                      draftCategories.length >= MAX_CATEGORIES ||
                      newCategoryLabel === ""
                    }
                  >
                    Add
                  </Button>
                </Stack>
                {draftCategories.length >= MAX_CATEGORIES && (
                  <Typography variant="caption" color="text.secondary">
                    Maximum of {MAX_CATEGORIES} categories reached.
                  </Typography>
                )}
                {draftCategories.length === 0 && (
                  <Typography variant="caption" color="error">
                    Add at least one category before creating the workspace.
                  </Typography>
                )}
              </Stack>
            </Collapse>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateWorkspace}
            variant="contained"
            disabled={
              creating ||
              !newWorkspaceName.trim() ||
              draftCategories.length === 0
            }
          >
            {creating ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {manageResourcesOpen !== undefined && (
        <ResourceManagementModal
          open={manageResourcesOpen}
          onClose={() => dispatch(closeResourceManagementModal())}
          initialTab={manageResourcesTab}
        />
      )}

      {currentWorkspaceId && (
        <ManageMembersModal
          open={manageMembersOpen}
          onClose={() => setManageMembersOpen(false)}
          workspaceId={currentWorkspaceId}
        />
      )}
    </Box>
  );
};

export default WorkspaceDropdown;
